import React, { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Card, CardContent } from './ui/card';
import { Phone, Mail, MapPin, MessageCircle, Send, CheckCircle2 } from 'lucide-react';
import { submitQuoteRequest, companyInfo } from '../data/mockData';
import { toast } from 'sonner';

export const Contact = () => {
  const [formData, setFormData] = useState({
    producto: '',
    volumen: '',
    ciudad: '',
    frecuencia: '',
    empaque: '',
    fechaRequerida: '',
    nombre: '',
    empresa: '',
    telefono: '',
    correo: '',
    mensaje: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await submitQuoteRequest(formData);
      setIsSubmitted(true);
      toast.success('¡Cotización enviada exitosamente! Nos pondremos en contacto pronto.');
      
      // Reset form after 3 seconds
      setTimeout(() => {
        setFormData({
          producto: '',
          volumen: '',
          ciudad: '',
          frecuencia: '',
          empaque: '',
          fechaRequerida: '',
          nombre: '',
          empresa: '',
          telefono: '',
          correo: '',
          mensaje: ''
        });
        setIsSubmitted(false);
      }, 3000);
    } catch (error) {
      toast.error('Error al enviar la cotización. Por favor intente de nuevo.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleWhatsApp = () => {
    const message = encodeURIComponent("Hola GONSO, quisiera cotizar productos/hortalizas para mi negocio");
    window.open(`https://wa.me/${companyInfo.whatsapp}?text=${message}`, '_blank');
  };

  return (
    <section id="contacto" className="py-20 bg-gradient-to-b from-white to-gray-50">
      <div className="container mx-auto px-4">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Solicite su Cotización
          </h2>
          <div className="w-24 h-1 bg-[#019645] mx-auto mb-6"></div>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Complete el formulario y nos pondremos en contacto a la brevedad para ofrecerle la mejor propuesta
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-12">
          {/* Contact Cards */}
          <Card className="border-none shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-2">
            <CardContent className="p-6 text-center">
              <div className="w-16 h-16 bg-[#019645]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Phone className="w-8 h-8 text-[#019645]" />
              </div>
              <h3 className="font-bold text-gray-900 mb-2">Teléfono</h3>
              <a href={`tel:${companyInfo.phone}`} className="text-[#019645] hover:text-[#077c38] transition-colors">
                {companyInfo.phone}
              </a>
            </CardContent>
          </Card>

          <Card className="border-none shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-2">
            <CardContent className="p-6 text-center">
              <div className="w-16 h-16 bg-[#019645]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Mail className="w-8 h-8 text-[#019645]" />
              </div>
              <h3 className="font-bold text-gray-900 mb-2">Correo Electrónico</h3>
              <a href={`mailto:${companyInfo.email}`} className="text-[#019645] hover:text-[#077c38] transition-colors">
                {companyInfo.email}
              </a>
            </CardContent>
          </Card>

          <Card className="border-none shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-2">
            <CardContent className="p-6 text-center">
              <div className="w-16 h-16 bg-[#019645]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <MessageCircle className="w-8 h-8 text-[#019645]" />
              </div>
              <h3 className="font-bold text-gray-900 mb-2">WhatsApp</h3>
              <button
                onClick={handleWhatsApp}
                className="text-[#019645] hover:text-[#077c38] transition-colors font-medium"
              >
                Enviar Mensaje
              </button>
            </CardContent>
          </Card>
        </div>

        {/* Form */}
        <Card className="border-none shadow-2xl max-w-4xl mx-auto">
          <CardContent className="p-8 md:p-12">
            {isSubmitted ? (
              <div className="text-center py-12">
                <CheckCircle2 className="w-20 h-20 text-[#019645] mx-auto mb-6" />
                <h3 className="text-3xl font-bold text-gray-900 mb-4">¡Gracias por su solicitud!</h3>
                <p className="text-xl text-gray-600">
                  Hemos recibido su cotización y nos pondremos en contacto muy pronto.
                </p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <Label htmlFor="producto">Producto de Interés *</Label>
                    <Select
                      name="producto"
                      value={formData.producto}
                      onValueChange={(value) => handleSelectChange('producto', value)}
                      required
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Seleccione un producto" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="tomate">Tomate Pony</SelectItem>
                        <SelectItem value="chile">Chile Nataly/Iguazú</SelectItem>
                        <SelectItem value="cebolla">Cebolla (varias variedades)</SelectItem>
                        <SelectItem value="varios">Varios productos</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label htmlFor="volumen">Volumen Aproximado *</Label>
                    <Input
                      id="volumen"
                      name="volumen"
                      placeholder="Ej: 500 lbs semanales"
                      value={formData.volumen}
                      onChange={handleChange}
                      required
                    />
                  </div>

                  <div>
                    <Label htmlFor="ciudad">Ciudad de Entrega *</Label>
                    <Input
                      id="ciudad"
                      name="ciudad"
                      placeholder="Ej: Tegucigalpa"
                      value={formData.ciudad}
                      onChange={handleChange}
                      required
                    />
                  </div>

                  <div>
                    <Label htmlFor="frecuencia">Frecuencia de Entrega *</Label>
                    <Select
                      name="frecuencia"
                      value={formData.frecuencia}
                      onValueChange={(value) => handleSelectChange('frecuencia', value)}
                      required
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Seleccione frecuencia" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="semanal">Semanal</SelectItem>
                        <SelectItem value="quincenal">Quincenal</SelectItem>
                        <SelectItem value="mensual">Mensual</SelectItem>
                        <SelectItem value="otro">Otro</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label htmlFor="empaque">Tipo de Empaque Preferido</Label>
                    <Input
                      id="empaque"
                      name="empaque"
                      placeholder="Ej: Cestas, sacos, cajas"
                      value={formData.empaque}
                      onChange={handleChange}
                    />
                  </div>

                  <div>
                    <Label htmlFor="fechaRequerida">Fecha Requerida</Label>
                    <Input
                      id="fechaRequerida"
                      name="fechaRequerida"
                      type="date"
                      value={formData.fechaRequerida}
                      onChange={handleChange}
                    />
                  </div>

                  <div>
                    <Label htmlFor="nombre">Nombre Completo *</Label>
                    <Input
                      id="nombre"
                      name="nombre"
                      placeholder="Su nombre"
                      value={formData.nombre}
                      onChange={handleChange}
                      required
                    />
                  </div>

                  <div>
                    <Label htmlFor="empresa">Empresa *</Label>
                    <Input
                      id="empresa"
                      name="empresa"
                      placeholder="Nombre de su empresa"
                      value={formData.empresa}
                      onChange={handleChange}
                      required
                    />
                  </div>

                  <div>
                    <Label htmlFor="telefono">Teléfono *</Label>
                    <Input
                      id="telefono"
                      name="telefono"
                      type="tel"
                      placeholder="+504 0000 0000"
                      value={formData.telefono}
                      onChange={handleChange}
                      required
                    />
                  </div>

                  <div>
                    <Label htmlFor="correo">Correo Electrónico *</Label>
                    <Input
                      id="correo"
                      name="correo"
                      type="email"
                      placeholder="correo@ejemplo.com"
                      value={formData.correo}
                      onChange={handleChange}
                      required
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="mensaje">Mensaje Adicional</Label>
                  <Textarea
                    id="mensaje"
                    name="mensaje"
                    placeholder="Cuéntenos más sobre sus necesidades..."
                    value={formData.mensaje}
                    onChange={handleChange}
                    rows={4}
                  />
                </div>

                <Button
                  type="submit"
                  size="lg"
                  disabled={isSubmitting}
                  className="w-full bg-[#019645] hover:bg-[#077c38] text-white text-lg py-6 transition-all duration-300 hover:scale-105 hover:shadow-lg"
                >
                  {isSubmitting ? (
                    'Enviando...'
                  ) : (
                    <>
                      <Send className="mr-2" size={20} />
                      Enviar Cotización
                    </>
                  )}
                </Button>
              </form>
            )}
          </CardContent>
        </Card>

        {/* Location */}
        <div className="mt-12 bg-white rounded-xl shadow-lg p-8 border-l-4 border-[#019645]">
          <div className="flex items-start">
            <MapPin className="w-8 h-8 text-[#cc1312] mr-4 flex-shrink-0 mt-1" />
            <div>
              <h3 className="text-2xl font-bold text-gray-900 mb-2">Ubicación</h3>
              <p className="text-lg text-gray-700">
                {companyInfo.location}
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
